import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Trash2, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import ConfirmModal from "../components/ConfirmModal";

const money = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(n || 0));

export default function Cart() {
  const navigate = useNavigate();
  const { items, totals, updateQty, removeItem, clearCart } = useCart();
  const [confirmClear, setConfirmClear] = useState(false);

  const goCheckout = () => navigate("/checkout");

  return (
    <main className="min-h-screen bg-white text-black">
      <section className="bg-[#1c1c1c] py-8 text-white">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 xl:px-6 pb-12 pt-28 md:pt-32">
          <h1 className="mt-3 text-4xl font-semibold tracking-tight md:text-5xl">
            Cart
          </h1>
          <p className="mt-3 text-white/70">
            Review your items and proceed to checkout.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16 xl:px-6 py-12">
        {items.length === 0 ? (
          <div className="rounded-3xl border border-black/10 bg-black/[0.03] p-10">
            <h2 className="text-xl font-semibold">Your cart is empty</h2>
            <p className="mt-2 text-black/60">
              Browse frames and add something you like.
            </p>
            <Link
              to="/shop"
              className="mt-6 inline-flex items-center justify-center rounded-2xl bg-pink-500 px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90 gap-2"
            >
Shop Frames
<ArrowRight size={18} aria-hidden="true" />
</Link>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-8">
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setConfirmClear(true)}
                  aria-label="Clear cart"
                  title="Clear cart"
                  className="flex h-9 w-9 items-center justify-center rounded-full text-black/50 transition hover:bg-pink-50 hover:text-pink-600"
                >
                  <Trash2 size={18} />
                </button>
              </div>

              {items.map((x) => {
                const unitPrice = Number(x.final_price ?? x.price ?? 0);
                const lineTotal = unitPrice * Number(x.qty || 1);
                const selectedOptions = Array.isArray(x.selected_options)
                  ? x.selected_options
                  : [];
                const uploadedFiles = Array.isArray(x.uploaded_files)
                  ? x.uploaded_files
                  : [];
                const customText =
                  typeof x.custom_text === "string" ? x.custom_text.trim() : "";

                return (
                  <div
                    key={x.key}
                    className="flex gap-4 rounded-3xl border border-black/10 bg-white p-5 md:p-6"
                  >
                    <div className="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-black/[0.04]">
                      {x.image ? (
                        <img
                          src={x.image}
                          alt={x.name}
                          className="h-full w-full object-cover"
                        />
                      ) : null}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="truncate font-semibold">{x.name}</p>

                          {selectedOptions.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {selectedOptions.map((option, index) => (
                                <p
                                  key={`${option.id || option.label || index}_${index}`}
                                  className="text-sm text-black/60"
                                >
                                  <span className="font-medium text-black/90">{option.group_name || 'Option'}:</span> {option.label}
                                  {Number(option.price) > 0 ? ` (+${money(option.price)})` : ""}
                                </p>
                              ))}
                            </div>
                          )}

                          {customText && (
                            <div className="mt-3 rounded-2xl bg-black/[0.03] px-3 py-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-black/45">
                                Custom text
                              </p>
                              <p className="mt-1 text-sm text-black/70 whitespace-pre-wrap">
                                {customText}
                              </p>
                            </div>
                          )}

                          {uploadedFiles.length > 0 && (
                            <div className="mt-3">
                              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-black/45">
                                Uploaded image{uploadedFiles.length > 1 ? "s" : ""}
                              </p>

                              <div className="flex flex-wrap gap-2">
                                {uploadedFiles.map((file, index) => {
                                  const preview =
                                    file?.preview ||
                                    (typeof file === "string" ? file : "");

                                  if (!preview) return null;

                                  return (
                                    <div
                                      key={`${file.name || "upload"}_${index}`}
                                      className="h-14 w-14 overflow-hidden rounded-xl border border-black/10 bg-black/[0.04]"
                                    >
                                      <img
                                        src={preview}
                                        alt={`Upload ${index + 1}`}
                                        className="h-full w-full object-cover"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => removeItem(x.key)}
                          aria-label="Remove item"
                          title="Remove item"
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-black/40 transition hover:bg-pink-50 hover:text-pink-600"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <label className="text-sm text-black/60">Qty</label>
                          <input
                            type="number"
                            min={1}
                            value={x.qty}
                            onChange={(e) => updateQty(x.key, e.target.value)}
                            className="w-20 rounded-2xl border border-black/15 px-3 py-2 text-sm outline-none focus:border-black/35"
                          />
                        </div>

                        <div className="text-right">
                          <p className="text-sm text-black/60">
                            {money(unitPrice)} each
                          </p>
                          <p className="text-lg font-semibold">
                            {money(lineTotal)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="rounded-2xl border border-black/15 px-5 py-3 text-sm font-semibold transition hover:border-black/30"
                >
                  Continue shopping
                </Link>
              </div>
            </div>

            <aside className="lg:col-span-4">
              <div className="rounded-3xl border border-black/10 bg-[#1c1c1c] p-6 text-white lg:sticky lg:top-6">
                <p className="text-[11px] uppercase tracking-[0.2em] text-white/60">
                  Summary
                </p>

                <div className="mt-4 flex items-center justify-between text-sm text-white/80">
                  <span>Items</span>
                  <span className="font-semibold text-white">{totals.count}</span>
                </div>

                <div className="mt-2 flex items-center justify-between text-sm text-white/80">
                  <span>Subtotal</span>
                  <span className="font-semibold text-white">
                    {money(totals.subtotal)}
                  </span>
                </div>

                <div className="mt-5 border-t border-white/15 pt-5">
                  <button
                    onClick={goCheckout}
                    className="w-full rounded-2xl bg-white px-6 py-3 text-sm font-semibold text-black transition hover:opacity-90"
                  >
                    Checkout
                  </button>
                  <p className="mt-3 text-xs text-white/60">
                    You’ll enter your details next.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        )}
      </section>
      <ConfirmModal
        open={confirmClear}
        title="Clear your cart?"
        message="This will remove all items from your cart."
        confirmLabel="Clear cart"
        onConfirm={() => {
          clearCart();
          setConfirmClear(false);
        }}
        onCancel={() => setConfirmClear(false)}
      />
    </main>
  );
}